var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Tag, Dropdown, Button, Checkbox, Tooltip, message } from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useStore } from '../../../../../../../store/rootStore';
import { getDirection } from '../../../../../../../helpers';
import styles from './Labels.module.scss';
const LabelsComponent = () => {
    const history = useHistory();
    const { labels: labelsStore, activeChatStore, chatsStore } = useStore();
    const { chat, chatAccess, isChatActive } = activeChatStore;
    const assignedIds = useMemo(() => (chat === null || chat === void 0 ? void 0 : chat.labels) || [], [chat === null || chat === void 0 ? void 0 : chat.labels]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [draft, setDraft] = useState(assignedIds);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!dropdownOpen) {
            setDraft(assignedIds);
        }
    }, [dropdownOpen, assignedIds]);
    useEffect(() => {
        if (labelsStore.labels.length === 0) {
            labelsStore.fetchLabels();
        }
    }, [labelsStore]);
    const draftSet = useMemo(() => new Set(draft), [draft]);
    const assignedSet = useMemo(() => new Set(assignedIds), [assignedIds]);
    const isDirty = draft.length !== assignedIds.length ||
        draft.some((id) => !assignedSet.has(id));
    const maxLabels = 5;
    const isLimitReached = draft.length >= maxLabels;
    const toggleDraft = (labelId) => {
        setDraft((prev) => prev.includes(labelId) ? prev.filter((id) => id !== labelId) : [...prev, labelId]);
    };
    const handleSave = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!chat || !isDirty || saving)
            return;
        setSaving(true);
        try {
            yield chatsStore.updateChatLabels(chat._id, { set: draft });
            setDropdownOpen(false);
        }
        catch (error) {
            message.error(React.createElement(Trans, { i18nKey: "labelsUpdateFailed" }));
        }
        finally {
            setSaving(false);
        }
    });
    const handleCancel = () => {
        setDraft(assignedIds);
        setDropdownOpen(false);
    };
    const handleRemoveLabel = (e, labelIdToRemove) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        if (!chat || saving)
            return;
        setSaving(true);
        try {
            yield chatsStore.updateChatLabels(chat._id, { remove: [labelIdToRemove] });
        }
        catch (error) {
            message.error(React.createElement(Trans, { i18nKey: "labelsUpdateFailed" }));
        }
        finally {
            setSaving(false);
        }
    });
    const handleManageLabels = () => {
        history.push('/manage/labels');
        setDropdownOpen(false);
    };
    if (!chat)
        return null;
    const canManageChat = (chatAccess === null || chatAccess === void 0 ? void 0 : chatAccess.manage) || false;
    const assignedLabels = assignedIds
        .map((id) => labelsStore.getLabelData(id))
        .filter((label) => !!label);
    const menu = {
        className: styles.addLabelDropdown,
        items: labelsStore.labels.map((label) => {
            const isChecked = draftSet.has(label.id);
            const shouldDisable = !isChecked && isLimitReached;
            return {
                key: label.id,
                className: `${styles.checkboxItem} ${shouldDisable ? styles.disabledItem : ''}`,
                style: { pointerEvents: 'none' },
                label: (React.createElement(Tooltip, { title: shouldDisable ? React.createElement(Trans, { i18nKey: "max5LabelsPerChat" }) : undefined, placement: "right" },
                    React.createElement("div", { className: `${styles.labelCheckboxItem} ${shouldDisable ? 'disabled' : ''}`, style: { pointerEvents: 'auto' }, onClick: (e) => {
                            e.stopPropagation();
                            if (!shouldDisable) {
                                toggleDraft(label.id);
                            }
                        } },
                        React.createElement(Checkbox, { checked: isChecked, disabled: shouldDisable, onChange: () => { } }),
                        React.createElement("div", { className: styles.labelColor, style: { backgroundColor: label.color } }),
                        React.createElement("span", { className: shouldDisable ? styles.disabledText : '' }, label.name)))),
            };
        }),
    };
    const renderDropdown = (menuNode) => (React.createElement("div", { className: styles.dropdownWrapper },
        React.createElement("div", { className: styles.dropdownHeader },
            React.createElement("span", null,
                React.createElement(Trans, { i18nKey: "assignLabels" })),
            draft.length > 0 && (React.createElement(Button, { onClick: () => setDraft([]), type: "default", className: styles.removeAllButton, danger: true, size: "small", disabled: saving },
                React.createElement(Trans, { i18nKey: "removeAll" })))),
        React.createElement("div", { className: styles.dropdownMenuScroll }, menuNode),
        isDirty && (React.createElement("div", { className: styles.dropdownFooter },
            React.createElement(Button, { size: "small", onClick: handleCancel, disabled: saving },
                React.createElement(Trans, { i18nKey: "cancel" })),
            React.createElement(Button, { size: "small", type: "primary", onClick: handleSave, loading: saving },
                React.createElement(Trans, { i18nKey: "save" })))),
        canManageChat && (React.createElement("div", { className: styles.manageLabelsFooter, onClick: handleManageLabels, role: "button", tabIndex: 0 },
            React.createElement(SettingOutlined, null),
            React.createElement("span", null,
                React.createElement(Trans, { i18nKey: "manageLabels" }))))));
    return (React.createElement("div", { className: styles.labelsContainer },
        React.createElement("div", { className: styles.header },
            React.createElement("span", { className: styles.title },
                React.createElement(Trans, { i18nKey: "labels" })),
            React.createElement(Tooltip, { title: !isChatActive ? React.createElement(Trans, { i18nKey: "sidePanelInactiveChatTooltip" }) : null, placement: "top" },
                React.createElement(Dropdown, { menu: menu, dropdownRender: renderDropdown, trigger: ['click'], autoFocus: false, destroyPopupOnHide: false, placement: getDirection() === 'rtl' ? 'bottomLeft' : 'bottomRight', autoAdjustOverflow: false, open: dropdownOpen, disabled: !isChatActive, onOpenChange: (open) => {
                        if (!open && isDirty && !saving)
                            return;
                        setDropdownOpen(open);
                    } },
                    React.createElement(Button, { type: "ghost", className: styles.addLabelButton, icon: React.createElement(PlusOutlined, null), disabled: !isChatActive })))),
        assignedLabels.length > 0 ? (React.createElement("div", { className: styles.tagsList }, assignedLabels.map((label) => {
            return (React.createElement(Tag, { key: label.id, color: label.color, className: styles.tagItem, onClose: (e) => handleRemoveLabel(e, label.id), closable: isChatActive }, label.name));
        }))) : (React.createElement("div", { className: styles.noTagsPlaceholder },
            React.createElement(Trans, { i18nKey: "noLabelsPlaceholder" })))));
};
export default observer(LabelsComponent);
